import { PresenceStatus } from "@common/types/presence-nats";

export type LastSeenByUserId = ReadonlyMap<number, number>;

interface ApplyPresenceMessageParams {
    lastSeen: LastSeenByUserId;
    userId: number;
    status: PresenceStatus;
    now: number;
}

interface ExpirePresenceParams {
    lastSeen: LastSeenByUserId;
    ttlMs: number;
    now: number;
}

/**
 * Heartbeats go out at two thirds of the TTL so one dropped or late heartbeat
 * does not expire the claim (task: 15s TTL -> 10s, chat: 3s TTL -> 2s).
 */
export const getPresenceHeartbeatIntervalMs = (ttlMs: number): number =>
    Math.floor((ttlMs * 2) / 3);

export const applyPresenceMessage = ({
    lastSeen,
    userId,
    status,
    now,
}: ApplyPresenceMessageParams): LastSeenByUserId => {
    const next = new Map(lastSeen);
    if (status === PresenceStatus.LEFT) {
        next.delete(userId);
    } else {
        next.set(userId, now);
    }
    return next;
};

export const expirePresence = ({
    lastSeen,
    ttlMs,
    now,
}: ExpirePresenceParams): LastSeenByUserId =>
    new Map([...lastSeen].filter(([, seenAt]) => now - seenAt <= ttlMs));

export const getPresentUserIds = (lastSeen: LastSeenByUserId): number[] =>
    [...lastSeen.keys()].sort((first, second) => first - second);

export interface ChannelPresence {
    lastSeen: ReadonlyMap<string, LastSeenByUserId>;
    /** Derived from `lastSeen`; a target's array keeps its identity until its viewers change */
    userIdsByTarget: ReadonlyMap<string, number[]>;
}

export const EMPTY_CHANNEL_PRESENCE: ChannelPresence = {
    lastSeen: new Map(),
    userIdsByTarget: new Map(),
};

export const NO_USER_IDS: number[] = [];

interface PresenceTargetKeyParams {
    workspaceId: string;
    targetId: string;
}

export const getPresenceTargetKey = ({
    workspaceId,
    targetId,
}: PresenceTargetKeyParams): string => `${workspaceId}/${targetId}`;

const isSameUserIds = (first: number[], second: number[]): boolean =>
    first.length === second.length &&
    first.every((userId, index) => userId === second[index]);

const replaceTarget = ({
    channel,
    targetKey,
    lastSeen,
}: {
    channel: ChannelPresence;
    targetKey: string;
    lastSeen: LastSeenByUserId;
}): ChannelPresence => {
    const nextLastSeen = new Map(channel.lastSeen);
    const nextUserIds = new Map(channel.userIdsByTarget);

    if (lastSeen.size === 0) {
        nextLastSeen.delete(targetKey);
        nextUserIds.delete(targetKey);
    } else {
        nextLastSeen.set(targetKey, lastSeen);
        const userIds = getPresentUserIds(lastSeen);
        const previous = channel.userIdsByTarget.get(targetKey);
        nextUserIds.set(
            targetKey,
            previous && isSameUserIds(previous, userIds) ? previous : userIds,
        );
    }

    return { lastSeen: nextLastSeen, userIdsByTarget: nextUserIds };
};

interface ApplyChannelMessageParams {
    channel: ChannelPresence;
    targetKey: string;
    userId: number;
    status: PresenceStatus;
    ttlMs: number;
    now: number;
}

export const applyChannelMessage = ({
    channel,
    targetKey,
    userId,
    status,
    ttlMs,
    now,
}: ApplyChannelMessageParams): ChannelPresence =>
    replaceTarget({
        channel,
        targetKey,
        lastSeen: applyPresenceMessage({
            lastSeen: expirePresence({
                lastSeen: channel.lastSeen.get(targetKey) ?? new Map(),
                ttlMs,
                now,
            }),
            userId,
            status,
            now,
        }),
    });

/** Returns the same channel when nothing expired, so callers can skip notifying. */
export const expireChannel = ({
    channel,
    ttlMs,
    now,
}: {
    channel: ChannelPresence;
    ttlMs: number;
    now: number;
}): ChannelPresence => {
    let next = channel;
    for (const [targetKey, lastSeen] of channel.lastSeen) {
        const unexpired = expirePresence({ lastSeen, ttlMs, now });
        if (unexpired.size !== lastSeen.size) {
            next = replaceTarget({
                channel: next,
                targetKey,
                lastSeen: unexpired,
            });
        }
    }
    return next;
};

export const formatTypingLabel = (names: string[]): string | null =>
    names.length > 0 ? `${names.join(", ")} typing…` : null;
